// © Microsoft Corporation. All rights reserved.

import { AppError, logException } from "@msnews/diagnostics";
import { ActivityLoggingService } from "clientcore-infrastructure-analytics/ActivityLoggingService";
import { BrowserConsoleAppender } from "clientcore-infrastructure-analytics/appenders/BrowserConsoleAppender";
import { ExponentialBackoffRetry } from "@msnews/core";
import { IInstrumentationService } from "clientcore-infrastructure-analytics/IInstrumentationService";
import { InstrumentationService } from "clientcore-infrastructure-analytics/InstrumentationService";
import { LogLevel } from "clientcore-infrastructure-analytics/models/LogLevel";
import { OneServiceUtility } from "./OneServiceUtility";
import { canUseDOM } from "@msnews/isomorphic-util";
import { logger } from "@msnews/diagnostics";

/**
 * Instrumentation service
 *
 * @type {IInstrumentationService}
 */
let instrumentationService: IInstrumentationService;

/**
 * Retry manager
 *
 * @type {ExponentialBackoffRetry}
 */
let exponentialBackoffRetry: ExponentialBackoffRetry;

/**
 * Number of times to retry requests
 */
const retryCount = 3;

/**
 * assets url
 */
const assetsBaseUrl = "assets.msn.com/service/";

/**
 * base Url for non-CN domain
 */
const apiBaseUrl = "api.msn.com/";

/**
 * Wrapper around window.fetch that on failure of the 1S Akamai endpoint (assets.msn.com/service),
 * tries the 1S the AFD endpoint (api.msn.com). Logs any exceptions.
 * Can be used by features to get backup support without re-writing code.
 * @param fetchUrl - string with the request url
 * @param requestParams - Initial Request params for the request
 * @returns {Promise<Response>} - A promise to fulfill the request
 */
export async function fetch1S(fetchUrl: string, requestParams: RequestInit, callerAppError: AppError): Promise<Response> {
    let response: Response;
    try {
        response = await fetch(fetchUrl, requestParams);
        if (response && response.ok) {
            return response;
        }
    } catch (error) {
        logException(error, callerAppError, "Exception calling 1S primary Url: " + fetchUrl);
        // Not returning so we can try the call through AFD
    }

    // response.ok is not okay, check if we have a specific FD header. If we have it, got to 1S so don't try the call again because
    // it will likely fail.
    if (response && response.headers && response.headers.get("OneWebServiceLatency")) {
        return response;
    }

    // If no response or exception try the AFD endpoint.
    let backupUrl: string;
    if (fetchUrl.indexOf(assetsBaseUrl) > 0) {
        backupUrl = fetchUrl.replace(assetsBaseUrl, apiBaseUrl);
    } else {
        // NOTE: Do not make the call if we do update the Url or we make the same failed call again.
        return response;
    }

    // NOTE: this WILL throw an exception like the current fetch() call.
    return await fetch(backupUrl, requestParams);
}

/**
 * Sends a request
 * If the request utilizes AbortController, provide the controller instance to abort the fetch with ExponentialBackoffRetry
 * @returns {Promise<Response>} - A promise to fulfill the request
 */
export async function sendRequest<T = Response>(request: () => Promise<T>, activityName: string): Promise<T> {
    const activity = getInstrumentationService().activityLoggingService.createActivity(activityName);
    return await OneServiceUtility.serviceRequestOnComplete<T>(
        () => getExponentialBackoffRetry().executeRequest(request, null, activity),
        () => getInstrumentationService().activityLoggingService.endActivity(activity));
}

/**
 * Get exponential backoff retry instance
 */
function getExponentialBackoffRetry(): ExponentialBackoffRetry {
    if (exponentialBackoffRetry) {
        return exponentialBackoffRetry;
    }

    // On server side retryCount wil be ignored
    exponentialBackoffRetry = new ExponentialBackoffRetry(getInstrumentationService().loggingService, retryCount);

    return exponentialBackoffRetry;
}

/**
 * Get instrumentation service instance
 */
function getInstrumentationService(): IInstrumentationService {
    if (instrumentationService) {
        return instrumentationService;
    }

    const activityService = ActivityLoggingService.getInstance("ExperienceActivityLogger");
    // Todo: use a different appender to log into mds tables
    if (canUseDOM() && logger.isDebug()) {
        activityService.addAppender(new BrowserConsoleAppender(LogLevel.Activity));
    }

    instrumentationService = new InstrumentationService(logger.getLoggingService(), activityService);

    return instrumentationService;
}
